/*
* Report page
*/

import * as KendoCharts from '@progress/kendo-react-charts';
import * as React from 'react';
import * as _ from 'lodash';
import { Table } from 'react-bootstrap';

import * as ReportCharts from '../../charts';
import { getComponentDisplayId } from '../../component-sorting';
import * as ReportComponents from '../report-components';

export const Render = ({ registerFunc, refFunc, scrollToFunc, topComponentCount = 4, appText, pageTitle = '', className = '', pageConfiguration, pageCount, reportData, ...rest }) => {
    const sectionName = ReportComponents.ReportSectionNames.significantComponentsGraph;
    const ReportPageWithRegister = ReportComponents.withPageRegistration(ReportComponents.ReportPageHeaderFooter, sectionName, registerFunc, refFunc, scrollToFunc);
    const getRowKey = (id, tableName = 'data') => ReportComponents.getRowKey(id, sectionName, tableName);

    const { formData, association, inventory, currentOrder, currentOrderChart, percentFunded, yearlySummaryData } = reportData;

    const sumAvgCostsPerUL = _.sumBy(inventory, 'expenditureMap[0].signAsDollar');
    const data = _(inventory)
        .orderBy('componentId')
        .map((inv, i) => {
            const invExpenditure = _.get(inv, 'expenditureMap[0]');

            let avgCost = _.get(inv, 'avgCost_sort', 0);
            let idealBal = _.get(invExpenditure, 'hypoProjectedExpenseShare', 0);
            let curBal = _.get(invExpenditure, 'projectedExpenseShare', 0);
            const usefulLife = _.get(inv, 'usefulLife_sort', 0);
            let avgCostPerUL = _.get(invExpenditure, 'signAsDollar', 0); // signasdollar

            let percent = 0;
            if (avgCostPerUL > 0 && sumAvgCostsPerUL > 0) {
                percent = (avgCostPerUL / sumAvgCostsPerUL);
            }
            if (inv.usefulLife === 'N/A') {
                idealBal = 0;
                curBal = 0;
                avgCostPerUL = 0;
                percent = 0;
            }

            // console.log(`sig-comp map compId ${inv.componentId} avgCostPerUL ${avgCostPerUL} sumAvgCostsPerUL ${sumAvgCostsPerUL} percent ${percent}`, { invExpenditure });

            return {
                _id: inv._id,
                componentDecimal: inv.componentDecimal,
                componentId: inv.componentId,
                componentName: inv.componentName,
                usefulLife: (inv.usefulLife === 0) ? 'N/A' : inv.usefulLife,
                remainingUsefulLife: inv.remainingUsefulLife,
                quantity: inv.quantity,
                avgCost: avgCost,
                avgCostPerUL: avgCostPerUL,
                idealBal: idealBal,
                curBal: curBal,
                percent: percent,
            };
        })
        .value();

    const rowData = _.orderBy(data, 'percent', ['desc']);

    let components = _.slice(rowData, 0, topComponentCount);
    let componentsTop = _.stubArray();

    if (_.size(rowData) > topComponentCount) {
        componentsTop = _.slice(rowData, topComponentCount);

        const sumTopComponentsAvgCostPerUL = _.sumBy(componentsTop, 'avgCostPerUL');
        const percentTop = (sumTopComponentsAvgCostPerUL > 0 && sumAvgCostsPerUL > 0) ? (sumTopComponentsAvgCostPerUL / sumAvgCostsPerUL) : 0;
        const sumAllComponentsAvgCostPerUL = _.sumBy(components, 'avgCostPerUL');

        components.push({
            _id: 'all-other',
            componentDecimal: undefined,
            componentId: 'All Other',
            componentName: 'See Expanded Table For Breakdown',
            usefulLife: '',
            remainingUsefulLife: '',
            quantity: '',
            avgCost: '',
            avgCostPerUL: sumAllComponentsAvgCostPerUL, // show total rather than other sum
            idealBal: 0,
            curBal: 0,
            percent: percentTop,
        });
    }

    const chartColors = ReportCharts.getBrandedPieChartColors();
    const chartData = ReportCharts.getSignificantComponentsChartData(appText, components, chartColors);
    const chartAxisLegendLabelFontConfig = '18px Arial, sans-serif';
    const chartAxisLabelFontConfig = 'bold 16px Arial, sans-serif';
    const chartLegendLabelColorConfig = 'color: #000000';
    const chartLabelContent = (c) => `${c.value}%`;

    const scChart = (
        <KendoCharts.Chart renderAs={pageConfiguration.kendoCharts.renderAs}>
            <KendoCharts.ChartTitle text="" />
            <KendoCharts.ChartArea height={600} />
            <KendoCharts.ChartLegend
                labels={{ font: chartAxisLegendLabelFontConfig, padding: 2, color: chartLegendLabelColorConfig }}
                position="top"
                padding={8}
                border={{ width: 2, color: '#000000' }}
                orientation="vertical"
            />
            <KendoCharts.ChartSeries>
                <KendoCharts.ChartSeriesItem
                    data={_.get(chartData, '[0].data', [])}
                    field="y"
                    categoryField="componentName"
                    type="pie"
                    highlight={{ visible: false }}
                    colorField="color"
                    explodeField="sliced"
                    overlay={{ gradient: 'glass' }}
                >
                    <KendoCharts.ChartSeriesLabels
                        visible={true}
                        content={chartLabelContent}
                        font={chartAxisLabelFontConfig}
                        color={chartLegendLabelColorConfig}
                        distance={50}
                        padding={2}
                        background="none"
                        position="center"
                        align="circle"
                    />
                </KendoCharts.ChartSeriesItem>
            </KendoCharts.ChartSeries>
        </KendoCharts.Chart>
    );

    const renderTable = (rows, ind, arr) => {
        return (
            <div key={`sig-comp-graph-table-${ind}`} className="pdf-page-significant-components-other">
                <Table condensed={true} bordered={true} responsive={false} className="pdf-data-table">
                    <thead>
                        <tr>
                            <th>ID #</th>
                            <th className="col-sm-4">Component Name</th>
                            <th>Useful Life (yrs.)</th>
                            <th>Remaining Useful Life (yrs.)</th>
                            <th>Average Current</th>
                            <th colSpan={2}>Significance: (Curr Cost/UL) AS %</th>
                        </tr>
                    </thead>
                    <tbody>
                        {
                            rows.map((inv, i) => (
                                <tr key={getRowKey(inv._id)}>
                                    <td className="col-sm-1 text-center">{getComponentDisplayId(inv)}</td>
                                    <td>{inv.componentName}</td>
                                    <td className="text-center">{inv.usefulLife}</td>
                                    <td className="text-center">{inv.remainingUsefulLife}</td>
                                    <td className="text-right"><ReportComponents.Currency value={inv.avgCost} decimals={0} hidden={inv.componentId === 'All Other'} /></td>
                                    <td className="text-right"><ReportComponents.Currency value={inv.avgCostPerUL} decimals={0} /></td>
                                    <td className="text-right"><ReportComponents.Percentage value={inv.percent} decimals={0} /></td>
                                </tr>
                            ))
                        }
                    </tbody>
                </Table>
            </div>
        );
    };

    return (
        <div>
            <ReportPageWithRegister title={pageTitle} className={className} totalPageCount={pageCount} showFooter={true} footerData={reportData}>
                <div className="pdf-page-chart">
                    {scChart}
                </div>

                {renderTable(components, 0, components)}
            </ReportPageWithRegister>
        </div>
    );
};



// WEBPACK FOOTER //
// ./src/report/pdf/pages/report-page-significant-components-graph-kendo.tsx